import React, { useState } from "react";
import { AgentMessageData, AgentMessageType } from "@/models";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypePrism from "rehype-prism";
import { Body1, Button, Tag, makeStyles, tokens, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Tooltip } from "@fluentui/react-components";
import { TaskService } from "@/services";
import { PersonRegular, DocumentSearchRegular, DatabaseRegular, PlayRegular } from "@fluentui/react-icons";
import { getAgentIcon, getAgentDisplayName } from '@/utils/agentIconUtils';
import { AgentMessageErrorBoundary } from '@/components/errors';
import { safeAgentAccess } from '@/utils/safePropertyAccess';

interface FullClause {
  document_name: string;
  section: string;
  document_type: string;
  icon: string;
  relevance: string;
  full_clause_text: string;
  score: number;
}

const useStyles = makeStyles({
  container: {
    maxWidth: '800px',
    margin: '0 auto 32px auto',
    padding: '0 24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    fontFamily: tokens.fontFamilyBase
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  humanAvatar: {
    backgroundColor: 'var(--colorBrandBackground)'
  },
  botAvatar: {
    backgroundColor: 'var(--colorNeutralBackground3)'
  },
  messageContent: {
    flex: 1,
    maxWidth: 'calc(100% - 48px)',
    display: 'flex',
    flexDirection: 'column'
  },
  humanMessageContent: {
    alignItems: 'flex-end'
  },
  botMessageContent: {
    alignItems: 'flex-start'
  },
  agentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  agentName: {
    fontWeight: '600',
    fontSize: '14px',
    color: 'var(--colorNeutralForeground1)',
    lineHeight: '20px'
  },
  messageBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.5',
    wordWrap: 'break-word'
  },
  humanBubble: {
    backgroundColor: 'var(--colorBrandBackground)',
    color: 'white !important',
    maxWidth: '80%',
    padding: '12px 16px',
    lineHeight: '1.5',
    alignSelf: 'flex-end'
  },
  botBubble: {
    backgroundColor: 'var(--colorNeutralBackground2)',
    color: 'var(--colorNeutralForeground1)',
    maxWidth: '100%',
    alignSelf: 'flex-start',
  },
  clarificationBubble: {
    backgroundColor: 'var(--colorNeutralBackground2)',
    color: 'var(--colorNeutralForeground1)',
    padding: '6px 8px', 
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.5',
    wordWrap: 'break-word',
    maxWidth: '100%',
    alignSelf: 'flex-start'
  },
  viewMoreLink: {
    marginTop: '8px',
    fontSize: '13px',
    color: 'var(--colorBrandForeground1)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  modalContent: {
    maxHeight: '50vh',
    overflowY: 'auto'
  },
  clauseItem: {
    marginBottom: '20px',
    padding: '16px',
    backgroundColor: 'var(--colorNeutralBackground2)',
    borderRadius: '8px',
    borderLeft: '4px solid var(--colorBrandBackground)'
  },
  clauseHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontWeight: '600',
    fontSize: '14px'
  },
  clauseText: {
    fontSize: '14px',
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap',
    color: 'var(--colorNeutralForeground1)'
  },
  clauseRelevance: {
    fontSize: '12px',
    color: 'var(--colorNeutralForeground3)',
    marginTop: '8px'
  }
});

// Check if message is a clarification request
const isClarificationMessage = (content: string): boolean => {
  const clarificationKeywords = [
    'need clarification',
    'please clarify',
    'could you provide more details',
    'i need more information',
    'please specify',
    'what do you mean by',
    'clarification about'
  ];
  const lowerContent = content.toLowerCase();
  return clarificationKeywords.some(keyword => lowerContent.includes(keyword));
};

// Check if this is a Research Analyst message with full clauses
const isResearchAnalystWithClauses = (agentName: string, msg: AgentMessageData): boolean => {
  const name = agentName?.toLowerCase() || '';
  const isRA = name.includes('research') || name === 'research_analyst' || name === 'research analyst';
  const hasClauses = !!(msg.extra_data?.full_clauses && msg.extra_data.full_clauses.length > 0);
  return isRA && hasClauses;
};

// Check if this is a Systems Analyst message with raw tool results
const isSystemsAnalystWithToolResults = (agentName: string, msg: AgentMessageData): boolean => {
  const name = agentName?.toLowerCase() || '';
  const isSA = name.includes('systems') || name === 'systems_analyst' || name === 'systems analyst';
  const rawResults = msg.extra_data?.raw_tool_results;
  const hasToolResults = Array.isArray(rawResults) ? rawResults.length > 0 : !!(rawResults && Object.keys(rawResults).length > 0);
  return isSA && hasToolResults;
};

// Get icon for tool result type
const getToolIcon = (toolType: string): string => {
  const iconMap: Record<string, string> = {
    invoice: '🧾',
    po: '📋',
    purchase_order: '📋',
    receipts: '📦',
    receipt: '📦',
    vendor: '🏢',
    payment: '💳',
    holds: '⚠️',
    hold: '⚠️',
    nimbello: '🔗',
    exception_summary: '📊',
    data_quality: '✅'
  };
  return iconMap[toolType.toLowerCase()] || '📄';
};

// Check if this is a planner/manager final message (workflow completed)
const isPlannerFinalMessage = (agentName: string, content: string): boolean => {
  const name = agentName?.toLowerCase() || '';
  const isPlannerAgent = name.includes('manager') || name.includes('planner') || name.includes('operations');
  const isFinalMessage = content.toLowerCase().includes('workflow completed') || content.includes('🎉');
  return isPlannerAgent && isFinalMessage;
};

// Single message component with modal state
const AgentMessageItem: React.FC<{
  msg: AgentMessageData;
  index: number;
  planData?: any;
  planApprovalRequest?: any;
}> = ({ msg, index, planData, planApprovalRequest }) => {
  const styles = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSAModalOpen, setIsSAModalOpen] = useState(false);

  const agentType = safeAgentAccess.type(msg);
  const content = safeAgentAccess.content(msg);
  const agentName = safeAgentAccess.name(msg);
  
  const isHuman = agentType === AgentMessageType.HUMAN_AGENT;
  const isClarification = !isHuman && isClarificationMessage(content);
  const showViewMore = !isHuman && isResearchAnalystWithClauses(agentName, msg);
  const showSAViewMore = !isHuman && isSystemsAnalystWithToolResults(agentName, msg);
  const showPlannerAction = !isHuman && isPlannerFinalMessage(agentName, content);
  const fullClauses: FullClause[] = msg.extra_data?.full_clauses || [];
  const rawToolResults: any[] = Array.isArray(msg.extra_data?.raw_tool_results) 
    ? msg.extra_data.raw_tool_results 
    : [];

  return (
    <AgentMessageErrorBoundary
      key={index}
      componentName="Individual Agent Message"
      showRetry={false}
    >
      <div
        className={styles.container}
        style={{ flexDirection: isHuman ? 'row-reverse' : 'row' }}
      >
        {/* Avatar */}
        <div className={`${styles.avatar} ${isHuman ? styles.humanAvatar : styles.botAvatar}`}>
          <AgentMessageErrorBoundary componentName="Agent Icon" showRetry={false}>
            {isHuman ? (
              <PersonRegular style={{ fontSize: '16px', color: 'white' }} />
            ) : (
              getAgentIcon(agentName, planData, planApprovalRequest)
            )}
          </AgentMessageErrorBoundary>
        </div>

        {/* Message Content */}
        <div className={`${styles.messageContent} ${isHuman ? styles.humanMessageContent : styles.botMessageContent}`}>
          {/* Agent Header (only for bots) */}
          {!isHuman && (
            <div className={styles.agentHeader}>
              <Body1 className={styles.agentName}>
                <AgentMessageErrorBoundary componentName="Agent Display Name" showRetry={false}>
                  {getAgentDisplayName(agentName)}
                </AgentMessageErrorBoundary>
              </Body1>
              <Tag appearance="brand">AI Agent</Tag>
            </div>
          )}

          {/* Message Bubble */}
          <AgentMessageErrorBoundary componentName="Message Content" showRetry={true}>
            <div className={
              isHuman 
                ? `${styles.messageBubble} ${styles.humanBubble}`
                : isClarification 
                  ? styles.clarificationBubble 
                  : `${styles.messageBubble} ${styles.botBubble}`
            }>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypePrism]}
                components={{
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      style={{ color: 'var(--colorNeutralBrandForeground1)', textDecoration: 'none' }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    />
                  )
                }}
              >
                {TaskService.cleanHRAgent(content) || ""}
              </ReactMarkdown>
            </div>
          </AgentMessageErrorBoundary>

          {/* View More button for Research Analyst */}
          {showViewMore && (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '12px' }}>
              <Button
                appearance="outline"
                icon={<DocumentSearchRegular />}
                onClick={() => setIsModalOpen(true)}
                style={{ 
                  border: '1.5px solid var(--colorBrandForeground1)',
                  color: 'var(--colorBrandForeground1)',
                  backgroundColor: 'transparent',
                  fontWeight: '500',
                  padding: '6px 16px'
                }}
              >
                View Details
              </Button>
            </div>
          )}

          {/* View Details button for Systems Analyst */}
          {showSAViewMore && (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '12px' }}>
              <Button
                appearance="outline"
                icon={<DatabaseRegular />}
                onClick={() => setIsSAModalOpen(true)}
                style={{ 
                  border: '1.5px solid var(--colorBrandForeground1)',
                  color: 'var(--colorBrandForeground1)',
                  backgroundColor: 'transparent',
                  fontWeight: '500',
                  padding: '6px 16px'
                }}
              >
                View Details
              </Button>
            </div>
          )}

          {/* Take Action button for Planner (disabled - read-only mode) */}
          {showPlannerAction && (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '12px' }}>
              <Tooltip 
                content="Action mode disabled. Agents are configured for query-only access. Enable action permissions in Settings to allow payment release, hold removal, and other write operations."
                relationship="description"
                positioning="above"
              >
                <Button
                  appearance="outline"
                  icon={<PlayRegular style={{ opacity: 0.4 }} />}
                  style={{ 
                    border: '1.5px solid var(--colorBrandForeground1)',
                    color: 'var(--colorBrandForeground1)',
                    backgroundColor: 'transparent',
                    fontWeight: '500',
                    padding: '6px 16px',
                    cursor: 'not-allowed'
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  Take Action
                </Button>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      {/* Modal for full clause text */}
      <Dialog open={isModalOpen} onOpenChange={(_, data) => setIsModalOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '550px', width: '80vw' }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DocumentSearchRegular style={{ fontSize: '20px' }} />
                Policy Documents Found
              </div>
            </DialogTitle>
            <DialogContent className={styles.modalContent}>
              {fullClauses.slice(0, 2).map((clause, idx) => (
                <div key={idx} className={styles.clauseItem}>
                  <div className={styles.clauseHeader}>
                    <span>{clause.icon}</span>
                    <span>{clause.document_name}</span>
                    {clause.section && <span>- {clause.section}</span>}
                  </div>
                  <div className={styles.clauseText}>
                    {clause.full_clause_text}
                  </div>
                  <div className={styles.clauseRelevance}>
                    Relevance: {clause.relevance} | Score: {(clause.score * 100).toFixed(1)}%
                  </div>
                </div>
              ))}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsModalOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Modal for Systems Analyst raw tool results */}
      <Dialog open={isSAModalOpen} onOpenChange={(_, data) => setIsSAModalOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '600px', width: '85vw' }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <DatabaseRegular style={{ fontSize: '20px' }} />
                API Data Retrieved
              </div>
            </DialogTitle>
            <DialogContent className={styles.modalContent}>
              {rawToolResults.map((toolResult, idx) => (
                <div key={idx} className={styles.clauseItem}>
                  <div className={styles.clauseHeader}>
                    <span>{getToolIcon(toolResult.tool || toolResult.display_name || '')}</span>
                    <span>{toolResult.display_name || toolResult.tool || 'Unknown Tool'}</span>
                  </div>
                  <div className={styles.clauseText} style={{ 
                    fontSize: '12px', 
                    fontFamily: 'monospace',
                    backgroundColor: 'var(--colorNeutralBackground3)',
                    padding: '12px',
                    borderRadius: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(toolResult.data || toolResult, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsSAModalOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </AgentMessageErrorBoundary>
  );
};

// Main render function
const renderAgentMessages = (
  agentMessages: AgentMessageData[], 
  planData?: any, 
  planApprovalRequest?: any
) => {
  if (!agentMessages?.length) return null;

  // Filter out messages with empty content
  const validMessages = agentMessages.filter(msg => {
    const content = safeAgentAccess.content(msg);
    return content && content.trim().length > 0;
  });
  if (!validMessages.length) return null;

  return (
    <AgentMessageErrorBoundary componentName="Streaming Agent Messages" showRetry={true}>
      <>
        {validMessages.map((msg, index) => (
          <AgentMessageItem
            key={index}
            msg={msg}
            index={index}
            planData={planData}
            planApprovalRequest={planApprovalRequest}
          />
        ))}
      </>
    </AgentMessageErrorBoundary>
  );
};

export default renderAgentMessages;
